import React from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import SimpleSettingsPage from "../../_suite/SimpleSettingsPage";
import HrPeopleDashboard from "../../hrPeople";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_HR_EMPLOYEE_TILE_AVATAR,
  HR_REQUISITION_FUNNEL_SEGMENT_STYLES
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function EmployeeMosaicPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.hr.listEmployees(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.hr.employees" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(e => (
            <Col xs="6" md="4" lg="3" key={e.id} className="mb-4">
              <Card className="text-center border-0 shadow-sm h-100">
                <CardBody>
                  <div
                    className="rounded-circle bg-light mx-auto mb-2 d-flex align-items-center justify-content-center text-primary font-weight-bold"
                    style={SUITE_HR_EMPLOYEE_TILE_AVATAR}
                  >
                    {e.name
                      .split(" ")
                      .map(x => x[0])
                      .join("")}
                  </div>
                  <h6 className="mb-0">{e.name}</h6>
                  <p className="small text-muted mb-2">{e.title}</p>
                  <Badge color="light" className="text-dark">
                    {e.dept}
                  </Badge>
                  {e.ptoBalanceHrs != null && (
                    <p className="small text-muted mt-2 mb-0">
                      PTO bank {e.ptoBalanceHrs}h
                    </p>
                  )}
                  <Button
                    tag={Link}
                    color="link"
                    size="sm"
                    className="d-block mx-auto"
                    to={e.id}
                    relative="path"
                  >
                    Profile
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function EmployeeProfilePage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.hr.getEmployee(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.hr.employee-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="4">
              <Card>
                <CardBody className="text-center">
                  <h3>{data.name}</h3>
                  <p>{data.title}</p>
                  <Badge>{data.dept}</Badge>
                  <p className="mt-2 mb-0">{data.location}</p>
                  {data.ptoBalanceHrs != null && (
                    <p className="mt-2 mb-0 small">
                      PTO balance: <strong>{data.ptoBalanceHrs}h</strong>
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="8">
              <Card>
                <CardBody>
                  <CardTitle tag="h5">HR file (demo)</CardTitle>
                  <p className="text-muted small">
                    Performance cycles, comp bands, and documents would load from
                    your HRIS.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function RequisitionsFunnelPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.hr.listRequisitions(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.hr.requisitions" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(r => (
          <Card key={r.id} className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between">
                <div>
                  <h5 className="mb-1">{r.title}</h5>
                  <div className="small text-muted">{r.dept}</div>
                </div>
                <Badge>{r.status}</Badge>
              </div>
              <div className="small mt-2">Open {r.daysOpen} days</div>
              {r.approvers && r.approvers.length > 0 && (
                <div className="small text-muted mt-1">
                  Chain: {r.approvers.join(" → ")}
                </div>
              )}
              <ProgressFunnel />
              <Button tag={Link} color="primary" size="sm" className="mt-2" to={r.id} relative="path">
                Pipeline
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ProgressFunnel() {
  return (
    <div className="d-flex gap-1 mt-2">
      {HR_REQUISITION_FUNNEL_SEGMENT_STYLES.map((segmentStyle, i) => (
        <div
          key={i}
          className="bg-primary rounded"
          style={segmentStyle}
        />
      ))}
    </div>
  );
}

function RequisitionDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.hr.getRequisition(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.hr.requisition-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.title}</h4>
              <p>
                {data.dept} · {data.status}
              </p>
              {data.approvers && (
                <ul className="small mb-0">
                  {data.approvers.map((a, i) => (
                    <li key={i}>{i + 1}. {a}</li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function TimeoffCalendarPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.hr.listTimeoff(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.hr.timeoff" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(t => (
            <Col md="6" key={t.id} className="mb-3">
              <Card className="border-start border-4 border-info">
                <CardBody>
                  <strong>{t.employee}</strong>
                  <div>
                    {t.type} · {t.range}
                    {t.hours != null && (
                      <span className="text-muted"> · {t.hours}h</span>
                    )}
                  </div>
                  <Badge>{t.status}</Badge>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function HrPeopleSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="hr-people" />}>
        <Route index element={<HrPeopleDashboard />} />
        <Route path="employees" element={<EmployeeMosaicPage />} />
        <Route path="employees/:id" element={<EmployeeProfilePage />} />
        <Route path="requisitions" element={<RequisitionsFunnelPage />} />
        <Route path="requisitions/:id" element={<RequisitionDetailPage />} />
        <Route path="timeoff" element={<TimeoffCalendarPage />} />
        <Route
          path="settings"
          element={
            <SimpleSettingsPage
              breadcrumbHeadingId="suite.hr.settings"
              titleId="suite.hr.settings"
              fetchSettings={suiteApi.hr.getHrSettings}
              onSave={suiteApi.hr.saveHrSettings}
              fields={[
                { name: "defaultLocation", labelId: "suite.hr.default-location" },
                { name: "workWeek", labelId: "suite.hr.work-week" }
              ]}
            />
          }
        />
      </Route>
    </Routes>
  );
}
